import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { LogOut, BarChart3, MousePointerClick, Bot, MapPin, ArrowLeft } from "lucide-react";
import { DESTINATIONS } from "../data/destinations";
import { useSeo } from "../hooks/useSeo";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "y2y_admin_token";

const titleOf = (id) => DESTINATIONS.find((d) => d.id === id)?.title || id;

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-brand-line bg-white p-6">
    <div className="flex items-center gap-3 text-brand-inkSoft">
      <Icon size={18} />
      <span className="overline">{label}</span>
    </div>
    <p className="font-display mt-3 text-4xl text-brand-ink">{value}</p>
  </div>
);

const Bar = ({ label, count, max }) => (
  <div className="flex items-center gap-4">
    <span className="w-36 shrink-0 truncate text-sm text-brand-ink">{label}</span>
    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-brand-bg2">
      <div className="h-full rounded-full bg-brand-terracotta" style={{ width: `${Math.max(6, (count / max) * 100)}%` }} />
    </div>
    <span className="w-10 shrink-0 text-right text-sm font-medium text-brand-ink">{count}</span>
  </div>
);

const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Login failed");
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-bg2 px-4">
      <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-xl">
        <p className="overline text-brand-inkSoft">YogaToYacht</p>
        <h1 className="font-display mt-2 text-3xl text-brand-ink">Admin sign in</h1>
        <p className="mt-1 text-sm text-brand-inkSoft">Analytics dashboard access.</p>
        <label className="mt-6 block">
          <span className="overline text-brand-inkSoft">Email</span>
          <input
            data-testid="admin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-2 w-full rounded-lg border border-brand-line bg-white px-3 py-2.5 text-sm text-brand-ink"
          />
        </label>
        <label className="mt-4 block">
          <span className="overline text-brand-inkSoft">Password</span>
          <input
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="mt-2 w-full rounded-lg border border-brand-line bg-white px-3 py-2.5 text-sm text-brand-ink"
          />
        </label>
        {error && <p data-testid="admin-login-error" className="mt-4 text-sm text-red-600">{error}</p>}
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading}
          className="mt-6 w-full rounded-full bg-brand-ink px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-brand-inkSoft disabled:opacity-50"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
        <Link to="/" className="mt-5 inline-flex items-center gap-1.5 text-sm text-brand-inkSoft hover:text-brand-ink">
          <ArrowLeft size={14} /> Back to site
        </Link>
      </form>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) return onLogout();
      const data = await res.json();
      setStats(data);
    } catch (e) {
      setErr("Could not load analytics.");
    }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  if (err) return <div className="p-10 text-brand-ink">{err}</div>;
  if (!stats) return <div data-testid="admin-loading" className="p-10 text-brand-inkSoft">Loading analytics…</div>;

  const maxPartner = Math.max(1, ...stats.clicks_by_partner.map((p) => p.count));
  const maxDest = Math.max(1, ...stats.top_destinations.map((p) => p.count));

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-brand-bg2">
      <header className="border-b border-brand-line bg-white">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-5 md:px-10">
          <div>
            <p className="overline text-brand-inkSoft">YogaToYacht</p>
            <h1 className="font-display text-2xl text-brand-ink">Analytics</h1>
          </div>
          <button
            data-testid="admin-logout"
            onClick={onLogout}
            className="inline-flex items-center gap-2 rounded-full border border-brand-line px-4 py-2 text-sm text-brand-ink hover:bg-brand-bg2"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-10 md:px-10">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <Stat testid="stat-clicks" icon={MousePointerClick} label="Affiliate clicks" value={stats.totals.clicks} />
          <Stat testid="stat-plans" icon={Bot} label="AI itineraries" value={stats.totals.plans} />
          <Stat testid="stat-chats" icon={BarChart3} label="AI chat logs" value={stats.totals.chats} />
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <section className="rounded-2xl border border-brand-line bg-white p-6">
            <h2 className="font-display text-xl text-brand-ink">Clicks by partner</h2>
            <div className="mt-5 space-y-4">
              {stats.clicks_by_partner.map((p) => (
                <Bar key={p.key} label={p.key} count={p.count} max={maxPartner} />
              ))}
              {stats.clicks_by_partner.length === 0 && <p className="text-sm text-brand-inkSoft">No clicks yet.</p>}
            </div>
          </section>

          <section className="rounded-2xl border border-brand-line bg-white p-6">
            <h2 className="font-display text-xl text-brand-ink">Top destinations</h2>
            <div className="mt-5 space-y-4">
              {stats.top_destinations.map((p) => (
                <Bar key={p.key} label={titleOf(p.key)} count={p.count} max={maxDest} />
              ))}
              {stats.top_destinations.length === 0 && <p className="text-sm text-brand-inkSoft">No data yet.</p>}
            </div>
          </section>
        </div>

        <section className="mt-8 rounded-2xl border border-brand-line bg-white p-6">
          <h2 className="font-display text-xl text-brand-ink">Recent affiliate clicks</h2>
          <div className="mt-4 overflow-x-auto">
            <table data-testid="recent-clicks-table" className="w-full text-left text-sm">
              <thead className="text-brand-inkSoft">
                <tr className="border-b border-brand-line">
                  <th className="py-2 pr-4 font-medium">Destination</th>
                  <th className="py-2 pr-4 font-medium">Partner</th>
                  <th className="py-2 pr-4 font-medium">Dates</th>
                  <th className="py-2 pr-4 font-medium">When</th>
                </tr>
              </thead>
              <tbody className="text-brand-ink">
                {stats.recent_clicks.map((c, i) => (
                  <tr key={i} className="border-b border-brand-line/60">
                    <td className="py-2.5 pr-4">
                      <span className="inline-flex items-center gap-1.5"><MapPin size={13} className="text-brand-inkSoft" />{titleOf(c.destination_id)}</span>
                    </td>
                    <td className="py-2.5 pr-4">{c.partner}</td>
                    <td className="py-2.5 pr-4 text-brand-inkSoft">{c.date_from || "—"}{c.date_to ? ` → ${c.date_to}` : ""}</td>
                    <td className="py-2.5 pr-4 text-brand-inkSoft">{c.ts ? new Date(c.ts).toLocaleString() : "—"}</td>
                  </tr>
                ))}
                {stats.recent_clicks.length === 0 && (
                  <tr><td colSpan={4} className="py-4 text-brand-inkSoft">No clicks recorded yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-8 rounded-2xl border border-brand-line bg-white p-6">
          <h2 className="font-display text-xl text-brand-ink">Recent AI itineraries</h2>
          <div className="mt-4 space-y-4">
            {stats.recent_chats.map((c, i) => (
              <div key={i} className="rounded-xl bg-brand-bg2 p-4">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className="font-medium text-brand-ink">{c.start_country || "—"}</span>
                  <span className="text-brand-inkSoft">· {c.days} days · {(c.trip_types || []).join(", ") || "any"}</span>
                  {c.feasible === false && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">no match</span>}
                </div>
                {c.summary && <p className="mt-2 text-sm text-brand-inkSoft">{c.summary}</p>}
                <p className="mt-1 text-xs text-brand-inkSoft">{(c.stops || []).map((s) => s.title).join(" → ")}</p>
              </div>
            ))}
            {stats.recent_chats.length === 0 && <p className="text-sm text-brand-inkSoft">No itineraries generated yet.</p>}
          </div>
        </section>
      </main>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  useSeo({ title: "Admin | YogaToYacht", path: "/admin", noindex: true });

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  if (!token) return <LoginScreen onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
